import type { Dispatch, FormEvent, SetStateAction } from 'react';

import type { SupportCase } from '../domain-types';

export function SupportPanel({
  visible,
  isPlatformAdmin,
  supportCases,
  supportSubject,
  supportDescription,
  supportPriority,
  supportResponses,
  supportBusy,
  onSubjectChange,
  onDescriptionChange,
  onPriorityChange,
  onResponsesChange,
  onCreate,
  onUpdate,
}: {
  visible: boolean;
  isPlatformAdmin: boolean;
  supportCases: SupportCase[];
  supportSubject: string;
  supportDescription: string;
  supportPriority: 'normal' | 'high';
  supportResponses: Record<string, string>;
  supportBusy: boolean;
  onSubjectChange: (value: string) => void;
  onDescriptionChange: (value: string) => void;
  onPriorityChange: (value: 'normal' | 'high') => void;
  onResponsesChange: Dispatch<SetStateAction<Record<string, string>>>;
  onCreate: (event: FormEvent<HTMLFormElement>) => Promise<void>;
  onUpdate: (supportCase: SupportCase, status: SupportCase['status']) => Promise<void>;
}) {
  if (!visible) return null;
  return (
    <section className="billing-panel" aria-labelledby="support-title">
      <div className="workspace-heading">
        <div>
          <p className="eyebrow">Customer support</p>
          <h2 id="support-title">Support cases</h2>
          <p className="brand-copy">
            Request assistance without granting quality.vet access to act as your account.
          </p>
        </div>
        <span>{supportCases.length} cases</span>
      </div>
      <div className="billing-grid">
        {!isPlatformAdmin ? (
          <form onSubmit={(event) => void onCreate(event)}>
            <label>
              Subject
              <input
                required
                minLength={5}
                maxLength={160}
                value={supportSubject}
                onChange={(event) => onSubjectChange(event.target.value)}
              />
            </label>
            <label>
              Description
              <textarea
                required
                minLength={10}
                maxLength={4000}
                rows={5}
                value={supportDescription}
                onChange={(event) => onDescriptionChange(event.target.value)}
              />
            </label>
            <label>
              Priority
              <select
                value={supportPriority}
                onChange={(event) => onPriorityChange(event.target.value as 'normal' | 'high')}
              >
                <option value="normal">Normal</option>
                <option value="high">High</option>
              </select>
            </label>
            <button disabled={supportBusy} type="submit">
              Create support case
            </button>
          </form>
        ) : (
          <p className="empty-state">
            You are viewing customer cases as a Super Administrator. Account impersonation is
            disabled.
          </p>
        )}
        <div className="subscription-summary">
          {supportCases.length === 0 ? (
            <p className="empty-state">No support cases have been recorded.</p>
          ) : (
            supportCases.map((supportCase) => (
              <div className="billing-row" key={supportCase.id}>
                <div>
                  <strong>{supportCase.subject}</strong>
                  <small>
                    {supportCase.priority} priority · {supportCase.status.replace('_', ' ')} ·{' '}
                    {new Date(supportCase.updatedAt).toLocaleString()}
                  </small>
                  <p>{supportCase.description}</p>
                  {supportCase.latestResponse ? (
                    <p>
                      <strong>quality.vet response:</strong> {supportCase.latestResponse}
                    </p>
                  ) : null}
                  {isPlatformAdmin && supportCase.status !== 'closed' ? (
                    <>
                      <label>
                        Customer-visible response
                        <textarea
                          minLength={10}
                          maxLength={4000}
                          rows={3}
                          value={supportResponses[supportCase.id] ?? ''}
                          onChange={(event) =>
                            onResponsesChange((current) => ({
                              ...current,
                              [supportCase.id]: event.target.value,
                            }))
                          }
                        />
                      </label>
                      <div className="tenant-lifecycle-actions">
                        {supportCase.status !== 'in_progress' ? (
                          <button
                            disabled={supportBusy}
                            type="button"
                            onClick={() => void onUpdate(supportCase, 'in_progress')}
                          >
                            Begin work
                          </button>
                        ) : null}
                        <button
                          disabled={supportBusy}
                          type="button"
                          onClick={() => void onUpdate(supportCase, 'resolved')}
                        >
                          Resolve
                        </button>
                        <button
                          disabled={supportBusy}
                          type="button"
                          onClick={() => void onUpdate(supportCase, 'closed')}
                        >
                          Close
                        </button>
                      </div>
                    </>
                  ) : null}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </section>
  );
}
